/**
 * reportCharts.js
 *
 * Off-screen Chart.js renderers for the clinician PDF report. Each returns
 * a base64 PNG data URL for one of the report's image slots.
 *
 * Design rules, all of them driven by how the report is actually used —
 * printed, photocopied, scanned, and compared against a previous visit:
 *
 *   Monochrome. Every series is black, grey or white; nothing carries
 *   meaning by hue alone, so a faxed copy says the same thing as the
 *   original.
 *
 *   Fixed y-ranges. Impedance and R-wave amplitude are pinned to clinical
 *   bounds rather than auto-scaled. A single artefactual sample — an open
 *   or shorted lead at implant reading 13 kΩ — would otherwise flatten
 *   every real measurement against the axis for the life of the device.
 *   Out-of-range samples are clamped to the ceiling, marked with a caret,
 *   and reported as ">1200 Ω" / ">20 mV" rather than as a number, because
 *   that is all such a reading actually tells you.
 *
 *   Average with a min/max whisker. A slot is one point at the mean with a
 *   bare vertical line spanning the range — no caps, no candlesticks, no
 *   connecting line. Reads cleanly whether a slot holds one sample or a
 *   week of them.
 *
 *   A fixed 78-week window. The x-axis always covers the same span, so two
 *   reports for the same patient — or for different patients — can be laid
 *   side by side. 78 weeks guarantees a full year of history is visible at
 *   an annual follow-up.
 *
 * Data mapping (Ditto features merged across the report window):
 *   Lead Impedance   ← leadimpedanceweeklymin/max .impedancevaluesohms
 *                      | .dailyimpedancevaluesohms | .weeklyimpedancevaluesohms
 *   R-Wave (mV)      ← rwaveamplitudeweeklymin/max .rwaveamplitudevaluesuv (µV)
 *   Battery Voltage  ← batteryvoltagetrendmin/max .voltagevaluesv
 *   Vent. markers    ← ventricularmarkers .vspercentage / .vppercentage,
 *                      sampled per revision and bucketed onto the time axis
 *   Rate histogram   ← sensehistogram / pacehistogram .percentagevalues
 *
 * "NaN" strings in the arrays are treated as gaps.
 */
import {
  Chart,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

Chart.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ── Time axis ────────────────────────────────────────────────────────
// Slot 0 is the most recent day and index grows into the past, matching
// the physician programmer. The first 14 slots are single days; the rest
// are whole weeks, giving 2 + 76 = 78 weeks of span.
const DAILY_MAX = 14;
const WEEK_SLOTS = 76;
const TOTAL_SLOTS = DAILY_MAX + WEEK_SLOTS;
const SPAN_WEEKS = 78;

// Slot index → week number, for slots in the weekly half.
const weekOfSlot = (i) => i - DAILY_MAX + 3;
const slotOfWeek = (w) => DAILY_MAX + w - 3;

const X_TICK_LABELS = (() => {
  const arr = new Array(TOTAL_SLOTS).fill("");
  arr[0] = "1D";
  arr[6] = "7D";
  arr[13] = "14D";
  // No 4W tick: slot 15 sits two slots from "14D" and the two labels
  // collide at this width.
  [13, 26, 39, 52, 65, 78].forEach((w) => {
    const i = slotOfWeek(w);
    if (i >= DAILY_MAX && i < TOTAL_SLOTS) arr[i] = `${w}W`;
  });
  return arr;
})();

const HIST_BINS = 18;
const HIST_LABELS = [
  "0", "<40", "50", "60", "70", "80", "90",
  "100", "110", "120", "130", "140", "150",
  "160", "170", "180", "190", "200>",
];

// ── Clinical y-ranges ────────────────────────────────────────────────
// Impedance and R-wave bounds are specified; the battery window covers a
// rechargeable cell's usable range with headroom on both sides.
const RANGE = {
  impedance: { min: 0, max: 1200, step: 200, unit: "Ω", title: "Impedance (Ω)" },
  rwave: { min: 0, max: 20, step: 4, unit: "mV", title: "R-Wave Amplitude (mV)" },
  battery: { min: 2.0, max: 4.5, step: 0.5, unit: "V", title: "Battery Voltage (V)" },
};

// ── Helpers ──────────────────────────────────────────────────────────
const parseNum = (v) => {
  if (v == null) return NaN;
  if (v === "NaN") return NaN;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
};

const parseArray = (raw) => (Array.isArray(raw) ? raw.map(parseNum) : []);
const anyFinite = (arr) => arr.some((v) => Number.isFinite(v));

// ── Monochrome palette ───────────────────────────────────────────────
const INK = "#000000";
const GRID = "rgba(0, 0, 0, 0.13)";
const PAPER = "#FFFFFF";
const MUTED = "#555555";
const SOLID = "#1A1A1A";   // "paced" / filled bars
const HOLLOW = "#FFFFFF";  // "sensed" / open bars, outlined in INK

// Type sizes in PDF points; the caller's scale factor converts them to
// canvas pixels so text lands at a predictable size after pdfme's
// downscale into the slot.
const TICK_PT = 7;
const TITLE_PT = 8;
const DATA_LINE_PT = 0.9;
const AXIS_LINE_PT = 0.7;
const px = (pt, scale) => Math.max(1, Math.round(pt * scale));

// ── Plugins ──────────────────────────────────────────────────────────
const whiteBackground = {
  id: "whiteBackground",
  beforeDraw: (chart) => {
    const { ctx, width, height } = chart;
    ctx.save();
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  },
};

const EMPTY_TEXT = "No data available for the selected date range";
const makeEmptyNotice = (scale) => ({
  id: "emptyNotice",
  afterDatasetsDraw: (chart) => {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.fillStyle = MUTED;
    ctx.font = `500 ${px(TITLE_PT, scale)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      EMPTY_TEXT,
      (chartArea.left + chartArea.right) / 2,
      (chartArea.top + chartArea.bottom) / 2
    );
    ctx.restore();
  },
});

// Marks where the x-axis changes resolution from daily to weekly. Grey and
// dashed so it reads as an annotation, not as data.
const makeResolutionDivider = (lineWidth) => ({
  id: "resolutionDivider",
  afterDatasetsDraw: (chart) => {
    const x = chart.scales.x, y = chart.scales.y;
    if (!x || !y) return;
    const px0 = x.getPixelForValue(DAILY_MAX - 0.5);
    if (!Number.isFinite(px0)) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = "rgba(0,0,0,0.45)";
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([lineWidth * 4, lineWidth * 3]);
    ctx.beginPath();
    ctx.moveTo(px0, y.top);
    ctx.lineTo(px0, y.bottom);
    ctx.stroke();
    ctx.restore();
  },
});

/**
 * Draws one marker per populated slot: a bare vertical line from min to
 * max with a filled dot at the mean. No end caps — the requirement is an
 * extending line, not an error bar with terminations.
 *
 * Samples above the axis ceiling are clamped by the caller; this plugin
 * puts a caret at the top of any clamped slot so a reader can see the
 * value ran past the scale instead of assuming it sat exactly on it.
 */
const makeRangeMarkers = ({ points, lineWidth, dotRadius }) => ({
  id: "rangeMarkers",
  afterDatasetsDraw: (chart) => {
    const xs = chart.scales.x, ys = chart.scales.y;
    if (!xs || !ys) return;
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
    ctx.lineWidth = lineWidth;
    for (const p of points) {
      const x = xs.getPixelForValue(p.i);
      if (!Number.isFinite(x)) continue;
      const yMin = ys.getPixelForValue(p.lo);
      const yMax = ys.getPixelForValue(p.hi);
      if (p.hi > p.lo) {
        ctx.beginPath();
        ctx.moveTo(x, yMin);
        ctx.lineTo(x, yMax);
        ctx.stroke();
      }
      const yAvg = ys.getPixelForValue(p.avg);
      ctx.beginPath();
      ctx.arc(x, yAvg, dotRadius, 0, Math.PI * 2);
      ctx.fill();
      if (p.clamped) {
        // Caret at the ceiling: "this ran off the top of the scale".
        const t = ys.getPixelForValue(ys.max);
        const s = dotRadius * 1.9;
        ctx.beginPath();
        ctx.moveTo(x - s, t + s);
        ctx.lineTo(x, t + s * 0.15);
        ctx.lineTo(x + s, t + s);
        ctx.stroke();
      }
    }
    ctx.restore();
  },
});

// ── Off-screen render ────────────────────────────────────────────────
const renderChartToBase64 = (config, width, height) =>
  new Promise((resolve) => {
    const div = document.createElement("div");
    div.style.cssText =
      "position:absolute; left:-9999px; top:-9999px; width:" +
      width + "px; height:" + height + "px;";
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    div.appendChild(canvas);
    document.body.appendChild(div);

    config.options = {
      ...(config.options || {}),
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      animations: { colors: false, x: false, y: false },
    };

    const chart = new Chart(canvas, config);
    const finish = () => {
      const url = chart.toBase64Image("image/png", 1.0);
      chart.destroy();
      document.body.removeChild(div);
      resolve(url);
    };
    requestAnimationFrame(() => requestAnimationFrame(finish));
  });

// ── Trend chart (impedance / R-wave / battery) ───────────────────────
/**
 * Collapse the raw min/max arrays into one marker per slot, clamping to
 * the axis ceiling and floor. `values` is optional single-series data
 * (used when only one array is available) — min and max then coincide and
 * the marker degenerates to a bare dot, which is the honest rendering.
 */
const buildMarkers = (minArr, maxArr, range) => {
  const points = [];
  let exceeded = false;
  // Impedance, amplitude and cell voltage are all strictly positive
  // quantities, so a zero in the array is the device's "no sample here"
  // sentinel rather than a reading. Plotting it would draw a floor of
  // false measurements along the bottom of every chart.
  const isSample = (v) => Number.isFinite(v) && v > 0;
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    const rawLo = minArr[i];
    const rawHi = maxArr[i];
    const haveLo = isSample(rawLo);
    const haveHi = isSample(rawHi);
    if (!haveLo && !haveHi) continue;
    const lo0 = haveLo ? rawLo : rawHi;
    const hi0 = haveHi ? rawHi : rawLo;
    const clamped = hi0 > range.max || lo0 > range.max;
    if (clamped) exceeded = true;
    const clamp = (v) => Math.min(range.max, Math.max(range.min, v));
    const lo = clamp(Math.min(lo0, hi0));
    const hi = clamp(Math.max(lo0, hi0));
    points.push({ i, lo, hi, avg: (lo + hi) / 2, clamped });
  }
  return { points, exceeded };
};

const buildTrendConfig = ({ points, range, scale, empty }) => ({
  type: "line",
  data: {
    labels: X_TICK_LABELS.map((l) => l || ""),
    // Chart.js needs a dataset to build the scales even though the markers
    // are painted by the plugin; an empty one keeps the axes honest.
    datasets: [{ label: "", data: [], borderWidth: 0, pointRadius: 0 }],
  },
  options: {
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: {
      x: {
        type: "linear",
        // Bounds must be whole slots: with a half-unit min, Chart.js
        // generates ticks at 0.5, 1.5 … and the integer-keyed label
        // lookup misses every one of them.
        min: 0,
        max: TOTAL_SLOTS - 1,
        ticks: {
          stepSize: 1,
          color: INK,
          font: { size: px(TICK_PT, scale), weight: "500" },
          callback: (v) =>
            Number.isInteger(v) && X_TICK_LABELS[v] ? X_TICK_LABELS[v] : "",
          autoSkip: false,
          maxTicksLimit: TOTAL_SLOTS + 2,
        },
        // A gridline per slot would be 90 verticals across 8 inches — the
        // plot reads as hatching. Only the labelled ticks get one.
        grid: {
          color: (c) => (X_TICK_LABELS[c?.tick?.value] ? GRID : "transparent"),
        },
        border: { color: INK, width: px(AXIS_LINE_PT, scale) },
      },
      y: {
        title: {
          display: true,
          text: range.title,
          color: INK,
          font: { size: px(TITLE_PT, scale), weight: "600" },
        },
        min: range.min,
        max: range.max,
        ticks: {
          stepSize: range.step,
          color: INK,
          font: { size: px(TICK_PT, scale), weight: "500" },
        },
        grid: { color: GRID },
        border: { color: INK, width: px(AXIS_LINE_PT, scale) },
      },
    },
  },
  plugins: empty
    ? [whiteBackground, makeEmptyNotice(scale)]
    : [
        whiteBackground,
        makeResolutionDivider(px(AXIS_LINE_PT, scale)),
        makeRangeMarkers({
          points,
          lineWidth: px(DATA_LINE_PT, scale),
          dotRadius: Math.max(1, px(DATA_LINE_PT * 1.6, scale)),
        }),
      ],
});

// ── Ventricular markers (% of time, paced vs sensed) ─────────────────
// One bar per time slot: paced filled from zero, sensed drawn hollow on
// top of it so the pair always reaches 100%. Stacking rather than pairing
// makes the split readable at a glance without a colour key.
const buildMarkersConfig = ({ paced, sensed, scale, empty }) => ({
  type: "bar",
  data: {
    labels: X_TICK_LABELS.map((l) => l || ""),
    datasets: [
      {
        label: "Paced",
        data: paced,
        backgroundColor: SOLID,
        borderColor: INK,
        borderWidth: 0,
        stack: "vm",
        barPercentage: 0.9,
        categoryPercentage: 1.0,
      },
      {
        label: "Sensed",
        data: sensed,
        backgroundColor: HOLLOW,
        borderColor: INK,
        borderWidth: px(AXIS_LINE_PT * 0.8, scale),
        stack: "vm",
        barPercentage: 0.9,
        categoryPercentage: 1.0,
      },
    ],
  },
  options: {
    plugins: {
      legend: {
        display: !empty,
        position: "top",
        labels: {
          color: INK,
          font: { size: px(TICK_PT, scale), weight: "500" },
          boxWidth: px(TICK_PT, scale),
        },
      },
      tooltip: { enabled: false },
    },
    scales: {
      x: {
        type: "category",
        stacked: true,
        ticks: {
          color: INK,
          font: { size: px(TICK_PT, scale), weight: "500" },
          callback: (_v, i) => X_TICK_LABELS[i] || "",
          autoSkip: false,
          maxTicksLimit: TOTAL_SLOTS + 2,
        },
        grid: { color: (c) => (X_TICK_LABELS[c?.index] ? GRID : "transparent") },
        border: { color: INK, width: px(AXIS_LINE_PT, scale) },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: "% of time",
          color: INK,
          font: { size: px(TITLE_PT, scale), weight: "600" },
        },
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          display: !empty,
          color: INK,
          font: { size: px(TICK_PT, scale), weight: "500" },
        },
        grid: { color: GRID },
        border: { color: INK, width: px(AXIS_LINE_PT, scale) },
      },
    },
  },
  plugins: empty ? [whiteBackground, makeEmptyNotice(scale)] : [whiteBackground],
});

// ── Rate histogram ───────────────────────────────────────────────────
// Bars rather than stepped lines: this is a histogram, and paired bars
// survive photocopying better than two overlaid outlines. Sensed is drawn
// hollow, paced solid, matching the markers plot's vocabulary.
const buildHistogramConfig = ({ sensePct, pacePct, scale, empty }) => {
  const clip = (arr) => {
    const out = new Array(HIST_BINS).fill(null);
    for (let i = 0; i < HIST_BINS; i++) {
      if (Number.isFinite(arr[i])) out[i] = Math.min(100, Math.max(0, arr[i]));
    }
    return out;
  };
  return {
    type: "bar",
    data: {
      labels: HIST_LABELS,
      datasets: [
        {
          label: "Sensed",
          data: clip(sensePct),
          backgroundColor: HOLLOW,
          borderColor: INK,
          borderWidth: px(AXIS_LINE_PT * 0.8, scale),
        },
        {
          label: "Paced",
          data: clip(pacePct),
          backgroundColor: SOLID,
          borderColor: INK,
          borderWidth: px(AXIS_LINE_PT * 0.8, scale),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: !empty,
          position: "top",
          labels: {
            color: INK,
            font: { size: px(TICK_PT, scale), weight: "500" },
            boxWidth: px(TICK_PT, scale),
          },
        },
        tooltip: { enabled: false },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Rate (bpm)",
            color: INK,
            font: { size: px(TITLE_PT, scale), weight: "600" },
          },
          ticks: { color: INK, font: { size: px(TICK_PT, scale), weight: "500" } },
          grid: { color: GRID },
          border: { color: INK, width: px(AXIS_LINE_PT, scale) },
        },
        y: {
          title: {
            display: true,
            text: "% of beats",
            color: INK,
            font: { size: px(TITLE_PT, scale), weight: "600" },
          },
          min: 0,
          max: 100,
          ticks: {
            stepSize: 20,
            display: !empty,
            color: INK,
            font: { size: px(TICK_PT, scale), weight: "500" },
          },
          grid: { color: GRID },
          border: { color: INK, width: px(AXIS_LINE_PT, scale) },
        },
      },
    },
    plugins: empty ? [whiteBackground, makeEmptyNotice(scale)] : [whiteBackground],
  };
};

// ── Marker series → time slots ───────────────────────────────────────
// `series` is [{ ts, vs, vp }] collected per SSE revision. Bucket each
// sample onto the slot it belongs to (day for the recent fortnight, week
// beyond that) and average within a slot.
const bucketMarkerSeries = (series, endMs) => {
  const acc = Array.from({ length: TOTAL_SLOTS }, () => ({ vp: 0, vs: 0, n: 0 }));
  const DAY = 86400000;
  for (const s of series || []) {
    const t = s?.ts ? Date.parse(s.ts) : NaN;
    if (!Number.isFinite(t)) continue;
    const vp = parseNum(s.vp);
    const vs = parseNum(s.vs);
    if (!Number.isFinite(vp) && !Number.isFinite(vs)) continue;
    const daysAgo = Math.floor((endMs - t) / DAY);
    if (daysAgo < 0) continue;
    let slot;
    if (daysAgo < DAILY_MAX) slot = daysAgo;
    else slot = slotOfWeek(Math.floor(daysAgo / 7) + 1);
    if (slot < 0 || slot >= TOTAL_SLOTS) continue;
    const a = acc[slot];
    a.vp += Number.isFinite(vp) ? vp : 0;
    a.vs += Number.isFinite(vs) ? vs : 0;
    a.n += 1;
  }
  const paced = new Array(TOTAL_SLOTS).fill(null);
  const sensed = new Array(TOTAL_SLOTS).fill(null);
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    const a = acc[i];
    if (!a.n) continue;
    let vp = a.vp / a.n;
    let vs = a.vs / a.n;
    const total = vp + vs;
    // Normalise to 100% — the pair is a split of the same beats, and the
    // device reports each side independently, so they can drift a little.
    if (total > 0) { vp = (vp / total) * 100; vs = (vs / total) * 100; }
    else { vp = 0; vs = 0; }
    paced[i] = +vp.toFixed(1);
    sensed[i] = +Math.max(0, 100 - vp).toFixed(1);
  }
  return { paced, sensed };
};

// Header value shown beside a plot title. Reports the ceiling breach
// rather than a number, because a clamped reading only tells you "at
// least this much".
const headline = (value, range, digits = 0) => {
  if (!Number.isFinite(value)) return "";
  if (value > range.max) return `>${range.max} ${range.unit}`;
  return `${value.toFixed(digits)} ${range.unit}`;
};

// ── Public API ───────────────────────────────────────────────────────
/**
 * @param {object} mergedFeatures  Ditto features merged over the window
 * @param {object} [opts]
 * @param {Array}  [opts.markerSeries]  [{ts, vs, vp}] per revision
 * @param {number} [opts.endMs]         window end, for bucketing the series
 * @returns {{images: object, headline: object}}
 */
export const renderReportCharts = async (mergedFeatures, opts = {}) => {
  const f = mergedFeatures || {};
  const endMs = Number.isFinite(opts.endMs) ? opts.endMs : Date.now();

  // ── Lead impedance ──
  const impMin = f.leadimpedanceweeklymin?.properties || {};
  const impMax = f.leadimpedanceweeklymax?.properties || {};
  const joinDailyWeekly = (props, dailyKey, weeklyKey, flatKey) => {
    const daily = parseArray(props[dailyKey]);
    const weekly = parseArray(props[weeklyKey]);
    if (daily.length || weekly.length) {
      const out = new Array(TOTAL_SLOTS).fill(NaN);
      daily.slice(0, DAILY_MAX).forEach((v, i) => { out[i] = v; });
      weekly.slice(0, WEEK_SLOTS).forEach((v, i) => { out[DAILY_MAX + i] = v; });
      return out;
    }
    return parseArray(props[flatKey]).slice(0, TOTAL_SLOTS);
  };
  const impLo = joinDailyWeekly(impMin, "dailyimpedancevaluesohms", "weeklyimpedancevaluesohms", "impedancevaluesohms");
  const impHi = joinDailyWeekly(impMax, "dailyimpedancevaluesohms", "weeklyimpedancevaluesohms", "impedancevaluesohms");
  const imp = buildMarkers(impLo, impHi.length ? impHi : impLo, RANGE.impedance);

  // ── R-wave amplitude (µV → mV) ──
  const uvToMv = (arr) => arr.map((v) => (Number.isFinite(v) ? v / 1000 : NaN));
  const rwLo = uvToMv(parseArray(f.rwaveamplitudeweeklymin?.properties?.rwaveamplitudevaluesuv)).slice(0, TOTAL_SLOTS);
  const rwHi = uvToMv(parseArray(f.rwaveamplitudeweeklymax?.properties?.rwaveamplitudevaluesuv)).slice(0, TOTAL_SLOTS);
  const rwave = buildMarkers(rwLo, rwHi.length ? rwHi : rwLo, RANGE.rwave);

  // ── Battery voltage ──
  const bvLo = parseArray(f.batteryvoltagetrendmin?.properties?.voltagevaluesv).slice(0, TOTAL_SLOTS);
  const bvHi = parseArray(f.batteryvoltagetrendmax?.properties?.voltagevaluesv).slice(0, TOTAL_SLOTS);
  const battery = buildMarkers(bvLo, bvHi.length ? bvHi : bvLo, RANGE.battery);

  // ── Ventricular markers ──
  const marks = bucketMarkerSeries(opts.markerSeries, endMs);
  const marksEmpty = !marks.paced.some((v) => v != null);

  // ── Rate histogram ──
  const sensePct = parseArray(f.sensehistogram?.properties?.percentagevalues);
  const pacePct = parseArray(f.pacehistogram?.properties?.percentagevalues);

  // Canvas size per chart, matched to its slot's aspect ratio in
  // util/config.js so pdfme never stretches the PNG. `slotMm` drives the
  // scale factor that keeps on-canvas type at its intended point size.
  const MM_TO_PT = 2.83465;
  const SLOT_MM = 196.99;
  const CANVAS_W = 2100;
  const CHARTS = {
    rwave: { w: CANVAS_W, h: 899 },
    flexarm: { w: CANVAS_W, h: 899 },
    battery: { w: CANVAS_W, h: 722 },
    markers: { w: CANVAS_W, h: 722 },
    histogram: { w: CANVAS_W, h: 790 },
  };
  const scale = CANVAS_W / (SLOT_MM * MM_TO_PT);

  const [impedanceUrl, rwaveUrl, batteryUrl, markersUrl, histogramUrl] = await Promise.all([
    renderChartToBase64(
      buildTrendConfig({ points: imp.points, range: RANGE.impedance, scale, empty: !imp.points.length }),
      CHARTS.flexarm.w, CHARTS.flexarm.h
    ),
    renderChartToBase64(
      buildTrendConfig({ points: rwave.points, range: RANGE.rwave, scale, empty: !rwave.points.length }),
      CHARTS.rwave.w, CHARTS.rwave.h
    ),
    renderChartToBase64(
      buildTrendConfig({ points: battery.points, range: RANGE.battery, scale, empty: !battery.points.length }),
      CHARTS.battery.w, CHARTS.battery.h
    ),
    renderChartToBase64(
      buildMarkersConfig({ paced: marks.paced, sensed: marks.sensed, scale, empty: marksEmpty }),
      CHARTS.markers.w, CHARTS.markers.h
    ),
    renderChartToBase64(
      buildHistogramConfig({
        sensePct, pacePct, scale,
        empty: !(anyFinite(sensePct) || anyFinite(pacePct)),
      }),
      CHARTS.histogram.w, CHARTS.histogram.h
    ),
  ]);

  // Most recent populated slot is slot 0 and index grows into the past,
  // so the first point in the list is the latest reading.
  const latestOf = (built) => (built.points.length ? built.points[0] : null);
  const impLatest = latestOf(imp);
  const rwLatest = latestOf(rwave);

  return {
    images: {
      leadImpedance: impedanceUrl,
      rwave: rwaveUrl,
      battery: batteryUrl,
      markers: markersUrl,
      histogram: histogramUrl,
    },
    headline: {
      impedance: impLatest
        ? (impLatest.clamped ? `>${RANGE.impedance.max} ${RANGE.impedance.unit}`
                             : headline(impLatest.avg, RANGE.impedance, 0))
        : "",
      rwave: rwLatest
        ? (rwLatest.clamped ? `>${RANGE.rwave.max} ${RANGE.rwave.unit}`
                            : headline(rwLatest.avg, RANGE.rwave, 2))
        : "",
    },
    spanWeeks: SPAN_WEEKS,
  };
};

export const REPORT_SPAN_WEEKS = SPAN_WEEKS;
